/**
 * The keys to your cars.
 *
 * There are no accounts in this game. DESIGN.md section 4: the plate is the
 * identity and a PIN is a car key, not a password, so owning several vehicles
 * is a keyring rather than a profile with a garage attached to it.
 *
 * That shape was chosen for privacy reasons and it turns out to be the mechanic.
 * Everything the network knows about you is attached to a plate: your habits,
 * your routine, the pharmacy on a Tuesday. A second plate is therefore a second
 * life, and the reconstruction enforcement can build of you has a hole in it the
 * exact size of whatever you did in the other car.
 *
 * Nothing in the game connects two of your cars to each other, because nothing
 * in the game *could*. There is no account for them to hang off. The only thing
 * that knows you own both is this file, on your own machine, which is the same
 * place the knowledge lives in life.
 */

const STORAGE_KEY = 'hotlist.keyring';
/** The old single-car key, read once so nobody loses the car they were driving. */
const LEGACY_KEY = 'hotlist.vehicle';

export interface CarKey {
  plate: string;
  pin: string;
  /** Wall-clock milliseconds when this car was last driven. */
  used: number;
}

export class Keyring {
  private keys: CarKey[] = [];

  constructor() {
    this.load();
  }

  private load(): void {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const held = JSON.parse(raw) as CarKey[];
        this.keys = held.filter(
          (key) => key && typeof key.plate === 'string' && typeof key.pin === 'string',
        );
      }
    } catch {
      this.keys = [];
    }

    // Carry over from before there was a keyring. Somebody mid-game should not
    // lose the car they were driving because the storage shape changed.
    if (this.keys.length === 0) {
      try {
        const raw = localStorage.getItem(LEGACY_KEY);
        if (raw) {
          const one = JSON.parse(raw) as { plate?: unknown; pin?: unknown };
          if (typeof one.plate === 'string' && typeof one.pin === 'string') {
            this.keys = [{ plate: one.plate, pin: one.pin, used: Date.now() }];
            this.save();
          }
        }
      } catch {
        /* nothing to carry over */
      }
    }
  }

  all(): readonly CarKey[] {
    return [...this.keys].sort((a, b) => b.used - a.used);
  }

  get size(): number {
    return this.keys.length;
  }

  /** The car most recently driven, which is the one to come back in. */
  current(): CarKey | null {
    return this.all()[0] ?? null;
  }

  has(plate: string): boolean {
    return this.keys.some((key) => key.plate === plate);
  }

  /** Add a car, or update the key for one already held. */
  add(plate: string, pin: string): void {
    const held = this.keys.find((key) => key.plate === plate);
    if (held) {
      held.pin = pin;
      held.used = Date.now();
    } else {
      this.keys.push({ plate, pin, used: Date.now() });
    }
    this.save();
  }

  /**
   * Put back plates the registry says open with a key already held.
   *
   * One PIN per account, so the key to the car being driven is the key to every
   * car on it. This is what makes the ring recoverable: clear site data, come
   * back with the one plate and key written down, and the rest arrive from the
   * server rather than being gone.
   *
   * Nothing is overwritten. A plate already on the ring keeps the key it has,
   * because the browser's copy is the one that has actually been used to open
   * something.
   */
  restore(plates: readonly string[], pin: string): void {
    if (!pin) return;
    let added = 0;
    for (const plate of plates) {
      if (this.keys.some((key) => key.plate === plate)) continue;
      // Older than anything driven, so restoring a ring does not reorder it.
      this.keys.push({ plate, pin, used: 0 });
      added++;
    }
    if (added > 0) this.save();
  }

  /** Every key gone. Signing out of a browser, in one move. */
  clear(): void {
    this.keys = [];
    this.save();
    try {
      localStorage.removeItem(LEGACY_KEY);
    } catch {
      // Storage may be unavailable; the empty list above is the part that counts.
    }
  }

  /** Mark a car as the one being driven now. */
  touch(plate: string): void {
    const held = this.keys.find((key) => key.plate === plate);
    if (!held) return;
    held.used = Date.now();
    this.save();
  }

  /** The key for a specific car, for switching into it. */
  keyFor(plate: string): CarKey | null {
    return this.keys.find((key) => key.plate === plate) ?? null;
  }

  /**
   * Let a car go.
   *
   * Losing the key means losing the car, which section 4 already accepted as
   * fine and thematically right. What it does not do is erase anything: every
   * read that car ever generated is still in the table under its plate, and the
   * person who gives up a key has not made themselves less findable, only less
   * able to drive it.
   */
  drop(plate: string): void {
    this.keys = this.keys.filter((key) => key.plate !== plate);
    this.save();
  }

  private save(): void {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.keys));
      // Kept in step so an older tab still finds the current car.
      const current = this.current();
      if (current) {
        localStorage.setItem(
          LEGACY_KEY,
          JSON.stringify({ plate: current.plate, pin: current.pin }),
        );
      }
    } catch {
      /* private browsing, or storage is full. The cars are simply not reclaimable. */
    }
  }
}
